import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { EngineRing } from '@/components/EngineRing';

const MIN_DURATION_MS = 5000;

/**
 * BODYMAX Engine Generation Overlay — Premium first-plan transition
 * 
 * Transition contract:
 *   1. Overlay appears instantly
 *   2. Generation runs under the overlay
 *   3. After generation: navigate to destination (onSuccess) — still under overlay
 *   4. Wait for MIN_DURATION AND destination ready signal
 *   5. One premium fade-out reveals the fully loaded destination
 * 
 * The user never sees dashboard/home/empty states between onboarding and their plan.
 */
export function EngineOverlay({ 
  visible, 
  title = 'BODYMAX ENGINE', 
  subtitle = 'Building your first personalized program',
  mode = 'build',
  week = 1,
  minutes = null,
  generationFn,
  onSuccess,
  onError,
  onDismiss,
  onExitToOnboarding,
  onRetry,
  waitForDestinationReady,
}) {
  const [phase, setPhase] = useState('idle'); // idle | running | navigating | fading | error
  const [errorMsg, setErrorMsg] = useState('');
  const mountedRef = useRef(true);
  const startedRef = useRef(false);
  const timerStartRef = useRef(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    if (!visible) {
      startedRef.current = false;
      timerStartRef.current = null;
    }
  }, [visible]);

  // Main transition flow
  useEffect(() => {
    if (!visible || startedRef.current || !generationFn) return;
    startedRef.current = true;
    setPhase('running');
    setErrorMsg('');

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!mountedRef.current) return;
        const t0 = Date.now();
        timerStartRef.current = t0;
        console.log(`[ENGINE] 1. overlay shown at ${t0}`);

        const run = async () => {
          // ── STEP 1: Generate ──
          let result = null;
          try {
            result = await generationFn();
          } catch (err) {
            console.log(`[ENGINE] GENERATION FAILED at ${Date.now() - t0}ms`);
            if (!mountedRef.current) return;
            setPhase('error');
            setErrorMsg(err?.response?.data?.detail || err?.message || 'Generation failed');
            onError?.(err);
            return;
          }
          const tGen = Date.now();
          console.log(`[ENGINE] 2. generation complete at ${tGen - t0}ms`);
          if (!mountedRef.current) return;

          // ── STEP 2: Wait for MIN_DURATION (visual presentation time) ──
          const elapsed = tGen - t0;
          const remaining = Math.max(0, MIN_DURATION_MS - elapsed);
          if (remaining > 0) {
            console.log(`[ENGINE] 3. holding ${remaining}ms for min duration`);
            await new Promise(r => setTimeout(r, remaining));
          }
          if (!mountedRef.current) return;

          // ── STEP 3: Navigate to destination (still under overlay) ──
          console.log(`[ENGINE] 4. navigating to destination at ${Date.now() - t0}ms`);
          setPhase('navigating');
          onSuccess?.(result);

          // ── STEP 4: Wait for destination to be ready ──
          if (waitForDestinationReady) {
            console.log(`[ENGINE] 5. waiting for destination ready...`);
            await waitForDestinationReady();
          } else {
            // Fallback: give destination 800ms to mount and fetch
            await new Promise(r => setTimeout(r, 800));
          }
          if (!mountedRef.current) return;

          // ── STEP 5: Fade out and reveal ──
          console.log(`[ENGINE] 6. fading at ${Date.now() - t0}ms`);
          setPhase('fading');
        };

        run();
      });
    });
  }, [visible, generationFn, onSuccess, onError, waitForDestinationReady]);

  const handleFadeComplete = useCallback(() => {
    if (phase === 'fading') {
      const t0 = timerStartRef.current || Date.now();
      console.log(`[ENGINE] 7. hidden at ${Date.now() - t0}ms — transition complete`);
      startedRef.current = false;
      timerStartRef.current = null;
      setPhase('idle');
      onDismiss?.();
    }
  }, [phase, onDismiss]);

  const handleExit = useCallback(() => {
    startedRef.current = false;
    timerStartRef.current = null;
    setPhase('idle');
    setErrorMsg('');
    onExitToOnboarding?.();
    onDismiss?.();
  }, [onExitToOnboarding, onDismiss]);

  // Retry is opt-in: the caller must hand us a fresh generation fn so we never
  // blindly re-POST the identical payload that just failed.
  const handleRetry = useCallback(() => {
    if (!onRetry) return;
    startedRef.current = false;
    setPhase('running');
    setErrorMsg('');
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!mountedRef.current) return;
        const t0 = Date.now();
        timerStartRef.current = t0;
        const run = async () => {
          let result = null;
          try { result = await onRetry(); } catch (err) {
            if (!mountedRef.current) return;
            setPhase('error');
            setErrorMsg(err?.response?.data?.detail || err?.message || 'Generation failed');
            onError?.(err);
            return;
          }
          if (!mountedRef.current) return;
          const elapsed = Date.now() - t0;
          const remaining = Math.max(0, MIN_DURATION_MS - elapsed);
          if (remaining > 0) await new Promise(r => setTimeout(r, remaining));
          if (!mountedRef.current) return;
          setPhase('navigating');
          onSuccess?.(result);
          if (waitForDestinationReady) await waitForDestinationReady();
          else await new Promise(r => setTimeout(r, 800));
          if (!mountedRef.current) return;
          setPhase('fading');
        };
        run();
      });
    });
  }, [onRetry, onSuccess, onError, waitForDestinationReady]);

  if (!visible && phase === 'idle') return null;

  return (
    <AnimatePresence>
      {(visible || phase !== 'idle') ? (
        <motion.div
          key="engine-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: phase === 'fading' ? 0 : 1 }}
          transition={{ duration: phase === 'fading' ? 0.7 : 0.2 }}
          onAnimationComplete={() => {
            if (phase === 'fading') handleFadeComplete();
          }}
          className="fixed inset-0 z-[100] flex flex-col items-center justify-center overflow-hidden"
          style={{ background: '#000000' }}
        >
          {/* Dark scrim keeps the copy legible over the ring bloom */}
          <div className="absolute inset-0 bg-black/30 pointer-events-none" />

          {/* Engine composition — vertically centered hero group */}
          <div className="relative z-10 flex flex-col items-center w-full px-6" style={{ marginTop: '-5vh' }}>
            
            {/* Engine ring — CSS/SVG, so it follows the active accent theme */}
            {phase !== 'error' && (
              <div className="mb-7" data-testid="engine-ring">
                <EngineRing mode={mode} week={week} minutes={minutes} />
              </div>
            )}

            {/* Text group — tight to engine */}
            {phase === 'error' ? (
              <div className="text-center space-y-3 mt-2">
                <div className="w-14 h-14 rounded-full bg-red-500/20 flex items-center justify-center mx-auto">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-red-500">
                    <circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/>
                  </svg>
                </div>
                <h2 className="text-xl font-heading font-bold uppercase text-white">Generation Failed</h2>
                <p className="text-base text-zinc-400 max-w-xs mx-auto">{errorMsg}</p>
                <div className="flex flex-col items-center gap-2.5 pt-2">
                  {onRetry ? (
                    <button
                      onClick={handleRetry}
                      data-testid="engine-try-again-btn"
                      className="px-8 py-3 rounded-full bg-accent text-accent-foreground font-heading font-bold uppercase tracking-wider text-sm active:scale-95 transition-transform"
                    >
                      Try Again
                    </button>
                  ) : null}
                  <button
                    onClick={handleExit}
                    data-testid="engine-back-to-setup-btn"
                    className={`px-8 py-3 rounded-full font-heading font-bold uppercase tracking-wider text-sm active:scale-95 transition-transform ${
                      onRetry
                        ? 'border border-white/25 text-white hover:bg-white/10'
                        : 'bg-accent text-accent-foreground'
                    }`}
                  >
                    Back to Setup
                  </button>
                </div>
              </div>
            ) : (
              <div className="text-center">
                <h2 className="text-2xl font-heading font-black tracking-[0.2em] uppercase mb-2" style={{ color: 'var(--accent-hex)' }}>{title}</h2>
                <p className="text-base text-zinc-400 mb-5 font-medium">{subtitle}</p>
                <div className="flex justify-center gap-2.5">
                  {[0, 1, 2].map(i => (
                    <div key={i} className="w-2.5 h-2.5 rounded-full bg-accent/50 animate-pulse" style={{ animationDelay: `${i * 0.3}s` }} />
                  ))}
                </div>
              </div>
            )}
          </div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
